/*
 * bandwidth-governor.ts -- Pure congestion policy for LiveKit video tracks.
 *
 * Per rule: think-in-systems (ai.knowledge/global) — policy stays independent of SDK objects.
 * Per rule: code-comments-required (ai.knowledge/global) — comments preserve non-obvious invariants.
 */

import { VideoQuality } from 'livekit-client';
// Domain-model id only (type-level import) — the policy stays free of SDK objects,
// but device ids use the branded Device['id'] so integration needs no casts.
import type { Device } from '@studio/models/device';

type DeviceId = Device['id'];

export type GovernorState = 'normal' | 'congested';

export type DeviceGovernorState = 'normal' | 'quality-capped' | 'paused-auto' | 'paused-manual';

/** Bounded reasons keep health decisions deterministic and analytics-safe. */
export type TrackHealthReason =
  | 'zero-bitrate'
  | 'low-quality'
  | 'stream-paused'
  | 'stale-sample'
  | 'fresh-sample-required';

export type TrackHealthStatus = 'healthy' | 'unhealthy' | 'unknown';

export interface TrackHealthInput {
  /** Current bitrate in bps; null means unavailable rather than zero. */
  bitrate: number | null;
  /** Normalized quality score from 0 to 1; null means unavailable. */
  quality: number | null;
  streamState: 'playing' | 'paused' | 'unknown';
  /** True only when the publication can actually accept a LOW quality cap. */
  canSetLowQuality?: boolean;
  /** Monotonic source generation; omitted callers receive a local generation. */
  sampleGeneration?: number;
}

export interface TrackHealthSnapshot {
  status: TrackHealthStatus;
  reasons: readonly TrackHealthReason[];
  sampleGeneration: number | null;
}

type GovernorActionDescriptor =
  | { type: 'set-quality'; deviceId: DeviceId; quality: VideoQuality }
  | { type: 'set-enabled'; deviceId: DeviceId; enabled: boolean }
  | { type: 'restore'; deviceId: DeviceId };

/** Proposal IDs are present on evaluate/onSelectedDeviceChanged output, but optional for reapply actions. */
export type GovernorAction = GovernorActionDescriptor & { proposalId?: number };

const CONGESTION_TRACK_THRESHOLD = 2;
const CONGESTION_ENTRY_DURATION_MS = 3_000;
const CONGESTION_EXIT_DURATION_MS = 8_000;
const ACTION_COOLDOWN_MS = 2_000;
// A freshly constructed governor (boot, or the reconnect rebuild) observes
// before it acts: automatic degradation waits for first samples and for the
// selection sync to catch up, so the selected tile is never paused inside the
// instance-recreation window.
const AUTO_DEGRADE_GRACE_MS = 5_000;
const BITRATE_UNHEALTHY_DURATION_MS = 2_000;
const QUALITY_UNHEALTHY_THRESHOLD = 0.3;
const QUALITY_UNHEALTHY_DURATION_MS = 3_000;
const SAMPLE_STALE_DURATION_MS = 5_000;

interface DeviceRecord {
  state: DeviceGovernorState;
  bitrateUnhealthySince: number | null;
  qualityUnhealthySince: number | null;
  latest: TrackHealthInput | null;
  sampleAt: number | null;
  sampleGeneration: number;
  minimumFreshGeneration: number;
}

interface SuccessfulTransition {
  deviceId: DeviceId;
  from: DeviceGovernorState;
  to: DeviceGovernorState;
}

type ProposalKind =
  | 'auto-degrade'
  | 'auto-recover'
  | 'manual-pause'
  | 'manual-restore'
  | 'selected-restore';

interface PendingProposal {
  action: GovernorAction & { proposalId: number };
  kind: ProposalKind;
  from: DeviceGovernorState;
  to: DeviceGovernorState;
}

type ProposalReference = number | GovernorAction;

/**
 * Stateful policy with injectable wall time. evaluate() may advance congestion
 * hysteresis, but applied device state changes only after commit().
 */
export class BandwidthGovernor {
  readonly #now: () => number;
  #selectedDeviceId: DeviceId | null = null;
  #visibleDevices: ReadonlySet<DeviceId> = new Set();
  #activeDevices = new Set<DeviceId>();
  #activeDevicesExplicit = false;
  #devices = new Map<DeviceId, DeviceRecord>();
  #state: GovernorState = 'normal';
  #congestionDetectedAt: number | null = null;
  #allObservableHealthySince: number | null = null;
  #lastActionAt: number | null = null;
  #suspendedAt: number | null = null;
  #suspendedDuration = 0;
  #manualLowBandwidth = false;
  #manualSnapshot: Map<DeviceId, DeviceGovernorState> | null = null;
  #manualPaused = new Set<DeviceId>();
  #transitionStack: SuccessfulTransition[] = [];
  #pending = new Map<number, PendingProposal>();
  #nextProposalId = 1;
  readonly #constructedAt: number;

  constructor(options?: { now?: () => number }) {
    this.#now = options?.now ?? (() => Date.now());
    this.#constructedAt = this.#now();
  }

  setSelectedDeviceId(deviceId: DeviceId | null): void {
    this.#selectedDeviceId = deviceId;
  }

  setVisibleDevices(deviceIds: ReadonlySet<DeviceId>): void {
    this.#visibleDevices = new Set(deviceIds);
  }

  /** Reconciliation is authoritative and removes disconnected devices from every policy structure. */
  reconcileActiveDevices(deviceIds: ReadonlySet<DeviceId>): void {
    this.#activeDevicesExplicit = true;
    this.#activeDevices = new Set(deviceIds);

    for (const deviceId of deviceIds) {
      this.#ensureDevice(deviceId);
    }

    for (const deviceId of [...this.#devices.keys()]) {
      if (!this.#activeDevices.has(deviceId)) {
        this.#removeDevice(deviceId);
      }
    }
  }

  /** Alias matching the other topology setters. */
  setActiveDevices(deviceIds: ReadonlySet<DeviceId>): void {
    this.reconcileActiveDevices(deviceIds);
  }

  setManualLowBandwidthMode(enabled: boolean): void {
    if (enabled === this.#manualLowBandwidth) return;

    this.#manualLowBandwidth = enabled;
    if (enabled) {
      this.#manualSnapshot = new Map(
        [...this.#activeDevices]
          .filter((deviceId) => deviceId !== this.#selectedDeviceId)
          .map((deviceId) => [deviceId, this.#ensureDevice(deviceId).state])
      );
      this.#manualPaused.clear();
    }
  }

  isManualLowBandwidthMode(): boolean {
    return this.#manualLowBandwidth;
  }

  updateTrackHealth(deviceId: DeviceId, stats: TrackHealthInput): void {
    if (!this.#activeDevicesExplicit) {
      this.#activeDevices.add(deviceId);
    } else if (!this.#activeDevices.has(deviceId)) {
      return;
    }

    const record = this.#ensureDevice(deviceId);
    const generation = stats.sampleGeneration ?? record.sampleGeneration + 1;

    // Duplicate or reordered samples must not extend a sustained-health interval.
    if (generation <= record.sampleGeneration) return;

    const now = this.#policyNow();
    record.sampleGeneration = generation;
    record.sampleAt = now;
    record.latest = { ...stats, sampleGeneration: generation };

    if (stats.bitrate === null || stats.bitrate === 0) {
      if (record.bitrateUnhealthySince === null) record.bitrateUnhealthySince = now;
    } else {
      record.bitrateUnhealthySince = null;
    }

    if (stats.quality !== null && stats.quality < QUALITY_UNHEALTHY_THRESHOLD) {
      if (record.qualityUnhealthySince === null) record.qualityUnhealthySince = now;
    } else {
      record.qualityUnhealthySince = null;
    }
  }

  /** Returns the current bounded health assessment without changing timers. */
  getTrackHealth(deviceId: DeviceId): TrackHealthSnapshot {
    const record = this.#devices.get(deviceId);
    return record
      ? this.#healthSnapshot(deviceId, record, this.#policyNow())
      : { status: 'unknown', reasons: ['fresh-sample-required'], sampleGeneration: null };
  }

  getDeviceHealth(deviceId: DeviceId): TrackHealthSnapshot {
    return this.getTrackHealth(deviceId);
  }

  /**
   * Propose work without claiming it succeeded. Pending proposals remain stable
   * until the integration explicitly commits or rejects each action.
   */
  evaluate(): GovernorAction[] {
    if (this.#suspendedAt !== null) return [];
    if (this.#pending.size > 0) return this.#pendingActions();

    const now = this.#policyNow();

    if (this.#manualLowBandwidth) {
      return this.#proposeManualPauses();
    }

    if (this.#manualSnapshot) {
      const restoreActions = this.#proposeManualRestores();
      if (restoreActions.length > 0) return restoreActions;

      // A still-selected camera stays normal; its old automatic stack must not re-degrade it.
      if (
        this.#selectedDeviceId &&
        this.#devices.get(this.#selectedDeviceId)?.state === 'normal' &&
        this.#manualSnapshot.has(this.#selectedDeviceId)
      ) {
        this.#transitionStack = this.#transitionStack.filter(
          (transition) => transition.deviceId !== this.#selectedDeviceId
        );
      }
      this.#manualSnapshot = null;
      this.#manualPaused.clear();
    }

    const participants = this.#healthParticipants();
    const snapshots = participants.map((deviceId) => ({
      deviceId,
      health: this.#healthSnapshot(deviceId, this.#ensureDevice(deviceId), now)
    }));
    const unhealthyCount = snapshots.filter(({ health }) => health.status === 'unhealthy').length;
    const observable = snapshots.filter(({ deviceId }) => !this.#isPolicyPaused(deviceId));
    // With every target paused, periodically unwind one transition to probe recovery.
    const allObservableHealthy =
      (observable.length === 0 && this.#transitionStack.length > 0) ||
      observable.every(({ health }) => health.status === 'healthy');

    this.#updateCongestionState(now, unhealthyCount, allObservableHealthy);

    if (this.#state !== 'congested' || !this.#isCooldownElapsed(now)) return [];

    if (allObservableHealthy && this.#allObservableHealthySince !== null) {
      if (now - this.#allObservableHealthySince >= CONGESTION_EXIT_DURATION_MS) {
        const recovery = this.#proposeRecovery();
        if (recovery.length > 0) return recovery;
        this.#state = 'normal';
        this.#allObservableHealthySince = null;
      }
      return [];
    }

    // Construction grace: never the recovery path above, only new degradation.
    if (now - this.#constructedAt < AUTO_DEGRADE_GRACE_MS) return [];

    return this.#proposeWorstDegradation(now);
  }

  /** Commit one proposal or a successful subset of a batch. */
  commit(proposal: ProposalReference | readonly ProposalReference[]): boolean {
    const references = Array.isArray(proposal) ? proposal : [proposal];
    return references.map((reference) => this.#settle(reference, true)).every(Boolean);
  }

  /** Reject one proposal or a failed subset without consuming cooldown or transition history. */
  reject(proposal: ProposalReference | readonly ProposalReference[]): boolean {
    const references = Array.isArray(proposal) ? proposal : [proposal];
    return references.map((reference) => this.#settle(reference, false)).every(Boolean);
  }

  getState(): GovernorState {
    return this.#state;
  }

  getDeviceState(deviceId: DeviceId): DeviceGovernorState {
    return this.#devices.get(deviceId)?.state ?? 'normal';
  }

  getDeviceIds(): DeviceId[] {
    return [...this.#devices.keys()].sort();
  }

  /** Committed auto-degrade stack depth — emitted on action analytics so LIFO recovery order is provable from events. */
  getTransitionStackDepth(): number {
    return this.#transitionStack.length;
  }

  onBlockAllVideo(): void {
    if (this.#suspendedAt === null) {
      this.#suspendedAt = this.#now();
    }
  }

  /** Suspension freezes policy time and requires a post-resume sample from every tracked device. */
  onUnblockAllVideo(): GovernorAction[] {
    if (this.#suspendedAt !== null) {
      this.#suspendedDuration += this.#now() - this.#suspendedAt;
      this.#suspendedAt = null;
      this.#requireFreshHealth(this.#devices.keys());
      this.#allObservableHealthySince = null;
    }
    return this.#reapplyCurrentState();
  }

  /** Selection restoration is also proposed so failed SDK work cannot corrupt policy state. */
  onSelectedDeviceChanged(newId: DeviceId | null, _previousId: DeviceId | null): GovernorAction[] {
    this.#selectedDeviceId = newId;
    if (!newId || this.#pending.size > 0) return this.#pendingActions();

    const record = this.#devices.get(newId);
    if (!record || record.state === 'normal') return [];

    return [
      this.#createProposal('selected-restore', newId, record.state, 'normal', {
        type: 'restore',
        deviceId: newId
      })
    ];
  }

  destroy(): void {
    this.#selectedDeviceId = null;
    this.#visibleDevices = new Set();
    this.#activeDevices.clear();
    this.#activeDevicesExplicit = false;
    this.#devices.clear();
    this.#state = 'normal';
    this.#congestionDetectedAt = null;
    this.#allObservableHealthySince = null;
    this.#lastActionAt = null;
    this.#suspendedAt = null;
    this.#suspendedDuration = 0;
    this.#manualLowBandwidth = false;
    this.#manualSnapshot = null;
    this.#manualPaused.clear();
    this.#transitionStack = [];
    this.#pending.clear();
    this.#nextProposalId = 1;
  }

  #policyNow(): number {
    const wallNow = this.#suspendedAt ?? this.#now();
    return wallNow - this.#suspendedDuration;
  }

  #ensureDevice(deviceId: DeviceId): DeviceRecord {
    let record = this.#devices.get(deviceId);
    if (!record) {
      record = {
        state: 'normal',
        bitrateUnhealthySince: null,
        qualityUnhealthySince: null,
        latest: null,
        sampleAt: null,
        sampleGeneration: 0,
        minimumFreshGeneration: 0
      };
      this.#devices.set(deviceId, record);
    }
    return record;
  }

  #removeDevice(deviceId: DeviceId): void {
    this.#devices.delete(deviceId);
    this.#manualSnapshot?.delete(deviceId);
    this.#manualPaused.delete(deviceId);
    this.#transitionStack = this.#transitionStack.filter((entry) => entry.deviceId !== deviceId);
    for (const [proposalId, pending] of this.#pending) {
      if (pending.action.deviceId === deviceId) this.#pending.delete(proposalId);
    }
  }

  #healthParticipants(): DeviceId[] {
    return [...this.#visibleDevices].filter((deviceId) => this.#activeDevices.has(deviceId)).sort();
  }

  #automaticTargets(): DeviceId[] {
    return this.#healthParticipants().filter((deviceId) => deviceId !== this.#selectedDeviceId);
  }

  #healthSnapshot(deviceId: DeviceId, record: DeviceRecord, now: number): TrackHealthSnapshot {
    const reasons: TrackHealthReason[] = [];
    const latest = record.latest;

    if (!latest || record.sampleGeneration <= record.minimumFreshGeneration) {
      reasons.push('fresh-sample-required');
    } else if (record.sampleAt !== null && now - record.sampleAt > SAMPLE_STALE_DURATION_MS) {
      reasons.push('stale-sample');
    }

    if (
      record.bitrateUnhealthySince !== null &&
      now - record.bitrateUnhealthySince >= BITRATE_UNHEALTHY_DURATION_MS
    ) {
      reasons.push('zero-bitrate');
    }

    if (
      record.qualityUnhealthySince !== null &&
      now - record.qualityUnhealthySince >= QUALITY_UNHEALTHY_DURATION_MS
    ) {
      reasons.push('low-quality');
    }

    if (latest?.streamState === 'paused' && this.#visibleDevices.has(deviceId)) {
      reasons.push('stream-paused');
    }

    const unhealthy = reasons.some(
      (reason) =>
        reason === 'zero-bitrate' || reason === 'low-quality' || reason === 'stream-paused'
    );
    const unknown = reasons.some(
      (reason) => reason === 'stale-sample' || reason === 'fresh-sample-required'
    );

    return {
      status: unhealthy
        ? 'unhealthy'
        : unknown
          ? 'unknown'
          : latest?.streamState === 'playing'
            ? 'healthy'
            : 'unknown',
      reasons,
      sampleGeneration: latest ? record.sampleGeneration : null
    };
  }

  #updateCongestionState(now: number, unhealthyCount: number, allObservableHealthy: boolean): void {
    if (this.#state === 'normal') {
      this.#allObservableHealthySince = null;
      if (unhealthyCount < CONGESTION_TRACK_THRESHOLD) {
        this.#congestionDetectedAt = null;
        return;
      }
      if (this.#congestionDetectedAt === null) this.#congestionDetectedAt = now;
      if (now - this.#congestionDetectedAt >= CONGESTION_ENTRY_DURATION_MS) {
        this.#state = 'congested';
        this.#congestionDetectedAt = null;
      }
      return;
    }

    if (!allObservableHealthy) {
      this.#allObservableHealthySince = null;
      return;
    }
    if (this.#allObservableHealthySince === null) this.#allObservableHealthySince = now;
  }

  #proposeWorstDegradation(now: number): GovernorAction[] {
    const candidates = this.#automaticTargets()
      .map((deviceId) => ({
        deviceId,
        record: this.#ensureDevice(deviceId),
        health: this.#healthSnapshot(deviceId, this.#ensureDevice(deviceId), now)
      }))
      .filter(({ record }) => record.state !== 'paused-auto' && record.state !== 'paused-manual')
      // Only provably unhealthy devices are degradation targets. Pausing on
      // 'unknown' (fresh-sample-required / stale-sample) re-paused every device
      // the recovery probe had just restored, so congestion could never exit.
      // Per rule: absent-data-gates-permissively (ai.knowledge/global) — no
      // evidence is never enforcement evidence.
      .filter(({ health }) => health.status === 'unhealthy')
      .sort((left, right) => this.#compareWorst(left, right));

    const candidate = candidates[0];
    if (!candidate) return [];

    if (candidate.record.state === 'normal' && candidate.record.latest?.canSetLowQuality === true) {
      return [
        this.#createProposal('auto-degrade', candidate.deviceId, 'normal', 'quality-capped', {
          type: 'set-quality',
          deviceId: candidate.deviceId,
          quality: VideoQuality.LOW
        })
      ];
    }

    return [
      this.#createProposal(
        'auto-degrade',
        candidate.deviceId,
        candidate.record.state,
        'paused-auto',
        { type: 'set-enabled', deviceId: candidate.deviceId, enabled: false }
      )
    ];
  }

  #compareWorst(
    left: { deviceId: DeviceId; record: DeviceRecord; health: TrackHealthSnapshot },
    right: { deviceId: DeviceId; record: DeviceRecord; health: TrackHealthSnapshot }
  ): number {
    const severity = (health: TrackHealthSnapshot) =>
      health.reasons.reduce(
        (score, reason) =>
          Math.max(
            score,
            reason === 'stream-paused'
              ? 3
              : reason === 'zero-bitrate'
                ? 2
                : reason === 'low-quality'
                  ? 1
                  : 0
          ),
        0
      );
    const severityDelta = severity(right.health) - severity(left.health);
    if (severityDelta !== 0) return severityDelta;

    const leftQuality = left.record.latest?.quality ?? Number.POSITIVE_INFINITY;
    const rightQuality = right.record.latest?.quality ?? Number.POSITIVE_INFINITY;
    if (leftQuality !== rightQuality) return leftQuality - rightQuality;

    const leftBitrate = left.record.latest?.bitrate ?? Number.POSITIVE_INFINITY;
    const rightBitrate = right.record.latest?.bitrate ?? Number.POSITIVE_INFINITY;
    if (leftBitrate !== rightBitrate) return leftBitrate - rightBitrate;

    return left.deviceId.localeCompare(right.deviceId);
  }

  #proposeRecovery(): GovernorAction[] {
    const transition = this.#transitionStack[this.#transitionStack.length - 1];
    if (!transition) return [];

    let action: GovernorActionDescriptor;
    if (transition.from === 'normal') {
      action = { type: 'restore', deviceId: transition.deviceId };
    } else if (transition.from === 'quality-capped') {
      action = {
        type: 'set-quality',
        deviceId: transition.deviceId,
        quality: VideoQuality.LOW
      };
    } else {
      action = { type: 'set-enabled', deviceId: transition.deviceId, enabled: false };
    }

    return [
      this.#createProposal(
        'auto-recover',
        transition.deviceId,
        transition.to,
        transition.from,
        action
      )
    ];
  }

  #proposeManualPauses(): GovernorAction[] {
    const actions: GovernorAction[] = [];
    if (this.#manualSnapshot === null) this.#manualSnapshot = new Map();

    for (const deviceId of [...this.#activeDevices].sort()) {
      if (deviceId === this.#selectedDeviceId || this.#manualPaused.has(deviceId)) continue;
      const record = this.#ensureDevice(deviceId);
      if ([...this.#pending.values()].some((pending) => pending.action.deviceId === deviceId))
        continue;
      if (!this.#manualSnapshot.has(deviceId)) this.#manualSnapshot.set(deviceId, record.state);
      actions.push(
        this.#createProposal('manual-pause', deviceId, record.state, 'paused-manual', {
          type: 'set-enabled',
          deviceId,
          enabled: false
        })
      );
    }
    return actions;
  }

  #proposeManualRestores(): GovernorAction[] {
    const actions: GovernorAction[] = [];
    for (const deviceId of [...this.#manualPaused].sort()) {
      const record = this.#devices.get(deviceId);
      const snapshot = this.#manualSnapshot?.get(deviceId);
      if (!record || snapshot === undefined) continue;

      const action: GovernorActionDescriptor =
        snapshot === 'normal'
          ? { type: 'restore', deviceId }
          : snapshot === 'quality-capped'
            ? { type: 'set-quality', deviceId, quality: VideoQuality.LOW }
            : { type: 'set-enabled', deviceId, enabled: false };
      actions.push(
        this.#createProposal('manual-restore', deviceId, 'paused-manual', snapshot, action)
      );
    }
    return actions;
  }

  #createProposal(
    kind: ProposalKind,
    deviceId: DeviceId,
    from: DeviceGovernorState,
    to: DeviceGovernorState,
    action: GovernorActionDescriptor
  ): GovernorAction & { proposalId: number } {
    const proposalId = this.#nextProposalId++;
    const proposed = { ...action, deviceId, proposalId } as GovernorAction & { proposalId: number };
    this.#pending.set(proposalId, { action: proposed, kind, from, to });
    return proposed;
  }

  #pendingActions(): GovernorAction[] {
    return [...this.#pending.values()]
      .sort((left, right) => left.action.proposalId - right.action.proposalId)
      .map(({ action }) => action);
  }

  #settle(reference: ProposalReference, succeeded: boolean): boolean {
    const proposalId = typeof reference === 'number' ? reference : reference.proposalId;
    if (proposalId === undefined) return false;
    const pending = this.#pending.get(proposalId);
    if (!pending) return false;
    this.#pending.delete(proposalId);
    if (!succeeded) return true;

    const record = this.#devices.get(pending.action.deviceId);
    if (!record) return false;

    switch (pending.kind) {
      case 'auto-degrade':
        if (record.state !== pending.from) return false;
        record.state = pending.to;
        this.#transitionStack.push({
          deviceId: pending.action.deviceId,
          from: pending.from,
          to: pending.to
        });
        this.#lastActionAt = this.#policyNow();
        this.#allObservableHealthySince = null;
        break;
      case 'auto-recover': {
        const top = this.#transitionStack[this.#transitionStack.length - 1];
        if (!top || top.deviceId !== pending.action.deviceId || top.to !== pending.from)
          return false;
        record.state = pending.to;
        this.#transitionStack.pop();
        this.#lastActionAt = this.#policyNow();
        this.#requireFreshHealth([pending.action.deviceId]);
        this.#allObservableHealthySince = null;
        break;
      }
      case 'manual-pause':
        record.state = 'paused-manual';
        this.#manualPaused.add(pending.action.deviceId);
        break;
      case 'manual-restore':
        record.state = pending.to;
        this.#manualPaused.delete(pending.action.deviceId);
        if (pending.to !== 'paused-auto') this.#requireFreshHealth([pending.action.deviceId]);
        this.#allObservableHealthySince = null;
        break;
      case 'selected-restore':
        record.state = 'normal';
        // Selection is temporary; stale membership would prevent re-pausing after focus moves.
        this.#manualPaused.delete(pending.action.deviceId);
        // Manual selection is temporary; its automatic snapshot still needs LIFO recovery.
        if (pending.from !== 'paused-manual') {
          this.#transitionStack = this.#transitionStack.filter(
            (transition) => transition.deviceId !== pending.action.deviceId
          );
        }
        this.#requireFreshHealth([pending.action.deviceId]);
        this.#allObservableHealthySince = null;
        break;
    }
    return true;
  }

  #requireFreshHealth(deviceIds: Iterable<DeviceId>): void {
    for (const deviceId of deviceIds) {
      const record = this.#devices.get(deviceId);
      if (!record) continue;
      record.minimumFreshGeneration = record.sampleGeneration;
      // Pre-restore evidence is void: a policy-paused device sends no media,
      // so its sustained-failure clocks and last sample describe a world that
      // no longer exists. Unhealthy must be re-earned from post-restore samples,
      // or restored devices bounce straight back to paused and recovery churns.
      record.bitrateUnhealthySince = null;
      record.qualityUnhealthySince = null;
      record.latest = null;
    }
  }

  #isPolicyPaused(deviceId: DeviceId): boolean {
    const state = this.#devices.get(deviceId)?.state;
    return state === 'paused-auto' || state === 'paused-manual';
  }

  #isCooldownElapsed(now: number): boolean {
    return this.#lastActionAt === null || now - this.#lastActionAt >= ACTION_COOLDOWN_MS;
  }

  #reapplyCurrentState(): GovernorAction[] {
    const actions: GovernorAction[] = [];
    for (const deviceId of this.getDeviceIds()) {
      const state = this.#devices.get(deviceId)!.state;
      if (state === 'quality-capped') {
        actions.push({ type: 'set-quality', deviceId, quality: VideoQuality.LOW });
      } else if (state === 'paused-auto' || state === 'paused-manual') {
        actions.push({ type: 'set-enabled', deviceId, enabled: false });
      }
    }
    return actions;
  }
}
